import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { AppIcon, Badge, Button, Notice, Plate, SelectField, TabPanel, Tabs, TextField } from "@wilco/ui";
import { useRef, useState, type ChangeEvent, type SubmitEvent } from "react";
import { formatDate, formatMoney, serverErrorText, t } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import {
  accountsQuery,
  articlesQuery,
  formatOf,
  MONEY,
  moneyApi,
  pendingQuery,
  supplierOrdersQuery,
  today,
  type Account,
  type ImportResult,
  type Transaction,
} from "./api.ts";

const route = getRouteApi("/app/money/bank");

export type BankTab = "accounts" | "pending";

/**
 * «Банк» (BNK-01..BNK-04): счета с балансом и выпиской файлом или из
 * Автоклієнта, очередь «не зіставлено» с разбором руками. Отправки денег нет:
 * платёж делают в банке (БН-06).
 */
export function BankPage() {
  const { tab = "accounts" } = route.useSearch();
  const navigate = useNavigate();
  const accounts = useQuery(accountsQuery);
  const pending = useQuery(pendingQuery);
  const [notice, setNotice] = useState<NoticeState | null>(null);

  return (
    <div className="page money">
      <div className="page-head">
        <h1 className="page-title page-title-icon">
          <AppIcon name="expense" size={32} />
          {t("bank.title")}
        </h1>
        <Button asChild variant="ghost">
          <Link to="/money/opex">{t("opex.title")}</Link>
        </Button>
      </div>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice">{notice.body}</Notice> : null}
      <Tabs<BankTab>
        value={tab}
        onValueChange={(v) => {
          setNotice(null);
          void navigate({ to: "/money/bank", search: { tab: v }, replace: true });
        }}
        label={t("bank.tabs")}
        items={[
          { value: "accounts", label: t("bank.tab.accounts"), count: accounts.data?.length },
          { value: "pending", label: t("bank.tab.pending"), count: pending.data?.length, alert: (pending.data?.length ?? 0) > 0 },
        ]}
      >
        <TabPanel value="accounts" className="queue">
          {accounts.error ? <Notice tone="danger" title={errorNotice(accounts.error).title} /> : null}
          {accounts.data?.length === 0 ? <p className="w-muted">{t("bank.accounts.empty")}</p> : null}
          {accounts.data?.map((a) => <AccountCard key={a.id} account={a} onNotice={setNotice} />)}
          <NewAccount onNotice={setNotice} />
        </TabPanel>
        <TabPanel value="pending" className="queue">
          {pending.data?.length === 0 ? (
            <div className="queue-empty">
              <p className="queue-empty-title">{t("bank.pending.empty")}</p>
              <p className="w-muted">{t("bank.pending.emptyLead")}</p>
            </div>
          ) : null}
          {pending.data?.map((tx) => <PendingRow key={tx.id} tx={tx} accounts={accounts.data ?? []} onNotice={setNotice} />)}
        </TabPanel>
      </Tabs>
    </div>
  );
}

const imported = (r: ImportResult): NoticeState =>
  r.error
    ? { tone: "danger", title: serverErrorText(r.error) ?? r.error }
    : {
        tone: r.pending > 0 ? "warning" : "success",
        title: t("bank.imported", { imported: r.imported, duplicates: r.duplicates }),
        body: r.pending > 0 ? t("bank.importedPending", { pending: r.pending }) : undefined,
      };

function AccountCard({ account, onNotice }: { account: Account; onNotice: (n: NoticeState | null) => void }) {
  const queryClient = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const done = {
    onSuccess: (r: ImportResult) => {
      onNotice(imported(r));
      void queryClient.invalidateQueries({ queryKey: MONEY });
    },
    onError: (e: unknown) => onNotice(errorNotice(e)),
  };
  const upload = useMutation({ mutationFn: ({ name, text }: { name: string; text: string }) => moneyApi.importStatement(account.id, formatOf(name), text) });
  const sync = useMutation({ mutationFn: () => moneyApi.sync(account.id, today(), today()) });

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    onNotice(null);
    upload.mutate({ name: f.name, text: await f.text() }, done);
  };

  return (
    <Plate cut="sm" bleed className="account">
      <div className="account-head">
        <span className="account-main">
          <span className="account-name">{account.name}</span>
          <span className="w-muted w-num account-iban">{account.iban}</span>
        </span>
        <span className="account-balance">
          {account.balance != null ? <span className="w-num">{formatMoney(account.balance, account.currency)}</span> : <span className="w-muted">{t("bank.noBalance")}</span>}
          {account.balanceOn ? <span className="w-muted">{t("bank.balanceOn", { date: formatDate(account.balanceOn) })}</span> : null}
        </span>
      </div>
      {account.lastError ? (
        <Notice tone="danger" title={serverErrorText(account.lastError) ?? account.lastError} />
      ) : null}
      <div className="account-actions">
        <Badge tone={account.api ? "strong" : "neutral"}>{t(account.api ? "bank.api" : "bank.file")}</Badge>
        <Button asChild variant="ghost">
          <Link to="/money/bank/accounts/$id" params={{ id: String(account.id) }}>
            {t("bank.transactions")}
          </Link>
        </Button>
        {account.api ? (
          <Button disabled={sync.isPending} onClick={() => sync.mutate(undefined, done)}>
            {t("bank.sync")}
          </Button>
        ) : null}
        <input ref={file} type="file" accept=".csv,.json,text/csv,application/json" hidden aria-label={t("bank.uploadFor", { account: account.name })} onChange={(e) => void pick(e)} />
        <Button variant="secondary" disabled={upload.isPending} onClick={() => file.current?.click()}>
          {t("bank.upload")}
        </Button>
      </div>
    </Plate>
  );
}

function NewAccount({ onNotice }: { onNotice: (n: NoticeState | null) => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ iban: "", name: "", currency: "UAH", tokenSecret: "" });
  const add = useMutation({ mutationFn: moneyApi.addAccount });
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    onNotice(null);
    add.mutate(
      { ...form, tokenSecret: form.tokenSecret.trim() || null },
      {
        onSuccess: (a) => {
          setForm({ iban: "", name: "", currency: "UAH", tokenSecret: "" });
          onNotice({ tone: "success", title: t("bank.accountAdded", { name: a.name }) });
          void queryClient.invalidateQueries({ queryKey: MONEY });
        },
        onError: (err) => onNotice(errorNotice(err)),
      },
    );
  };
  return (
    <Plate cut="sm" bleed>
      <form className="money-form" onSubmit={submit}>
        <h2 className="money-form-title">{t("bank.newAccount")}</h2>
        <TextField label={t("bank.iban")} value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} required autoComplete="off" />
        <TextField label={t("bank.accountName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <SelectField
          label={t("bank.currency")}
          value={form.currency}
          onChange={(e) => setForm({ ...form, currency: e.target.value })}
          options={["UAH", "USD", "EUR"].map((c) => ({ value: c, label: c }))}
        />
        <TextField label={t("bank.tokenSecret")} hint={t("bank.tokenSecretHint")} value={form.tokenSecret} onChange={(e) => setForm({ ...form, tokenSecret: e.target.value })} autoComplete="off" />
        <Button type="submit" variant="create" disabled={add.isPending}>
          {t("bank.addAccount")}
        </Button>
      </form>
    </Plate>
  );
}

type Action = "order" | "opex" | "ignore";

/**
 * Платёж «не зіставлено» (БН-03): списание на заказ поставщику или в
 * «Витрати» статьёй, любой платёж можно пропустить с причиной.
 */
function PendingRow({ tx, accounts, onNotice }: { tx: Transaction; accounts: Account[]; onNotice: (n: NoticeState | null) => void }) {
  const queryClient = useQueryClient();
  const outgoing = tx.amount < 0;
  const [action, setAction] = useState<Action | null>(null);
  const [order, setOrder] = useState("");
  const [article, setArticle] = useState("");
  const [family, setFamily] = useState("");
  const [reason, setReason] = useState("");
  const orders = useQuery({ ...supplierOrdersQuery, enabled: action === "order" });
  const articles = useQuery({ ...articlesQuery, enabled: action === "opex" });
  const resolve = useMutation({
    mutationFn: async (): Promise<Transaction> => {
      if (action === "order") return moneyApi.matchSupplier(tx.id, order);
      if (action === "opex") return moneyApi.allocate(tx.id, { article, family: family.trim() || null });
      return moneyApi.ignore(tx.id, reason);
    },
  });
  const account = accounts.find((a) => a.id === tx.account);

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    onNotice(null);
    resolve.mutate(undefined, {
      onSuccess: () => {
        onNotice({ tone: "success", title: t("bank.resolved", { counterparty: tx.counterparty || tx.purpose }) });
        void queryClient.invalidateQueries({ queryKey: MONEY });
      },
      onError: (err) => onNotice(errorNotice(err)),
    });
  };

  return (
    <Plate cut="sm" bleed className="pending">
      <div className="pending-head">
        <span className="pending-main">
          <span className="pending-who">{tx.counterparty || t("bank.noCounterparty")}</span>
          <span className="w-muted">{[formatDate(tx.date), tx.edrpou ? t("bank.edrpou", { code: tx.edrpou }) : null, account?.name].filter(Boolean).join(" · ")}</span>
          <span className="pending-purpose">{tx.purpose}</span>
          {tx.note ? <span className="w-muted">{serverErrorText(tx.note) ?? tx.note}</span> : null}
        </span>
        <span className="w-num pending-sum" data-out={outgoing ? "" : undefined}>
          {formatMoney(tx.amount, tx.currency)}
        </span>
      </div>
      <div className="pending-actions" role="group" aria-label={t("bank.resolve")}>
        {outgoing ? (
          <Button variant={action === "order" ? "primary" : "secondary"} aria-pressed={action === "order"} onClick={() => setAction("order")}>
            {t("bank.toOrder")}
          </Button>
        ) : null}
        {outgoing ? (
          <Button variant={action === "opex" ? "primary" : "secondary"} aria-pressed={action === "opex"} onClick={() => setAction("opex")}>
            {t("bank.toOpex")}
          </Button>
        ) : null}
        <Button variant={action === "ignore" ? "primary" : "secondary"} aria-pressed={action === "ignore"} onClick={() => setAction("ignore")}>
          {t("bank.skip")}
        </Button>
      </div>
      {action ? (
        <form className="money-form money-form-inline" onSubmit={submit}>
          {action === "order" ? (
            <SelectField
              label={t("bank.order")}
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              required
              options={[
                { value: "", label: orders.data?.length === 0 ? t("bank.noOrders") : t("bank.chooseOrder") },
                ...(orders.data ?? []).map((o) => ({
                  value: o.id,
                  label: `${o.number} · ${o.supplier} · ${formatMoney(o.gross - o.paid, o.currency)}${o.invoice ? ` · ${o.invoice}` : ""}`,
                })),
              ]}
            />
          ) : null}
          {action === "opex" ? (
            <>
              <SelectField
                label={t("opex.article")}
                value={article}
                onChange={(e) => setArticle(e.target.value)}
                required
                options={[{ value: "", label: t("opex.chooseArticle") }, ...(articles.data ?? []).map((a) => ({ value: a.code, label: a.name }))]}
              />
              <TextField label={t("opex.family")} hint={t("opex.familyHint")} value={family} onChange={(e) => setFamily(e.target.value)} autoComplete="off" />
            </>
          ) : null}
          {action === "ignore" ? <TextField label={t("bank.reason")} value={reason} onChange={(e) => setReason(e.target.value)} required /> : null}
          <Button type="submit" variant="create" disabled={resolve.isPending}>
            {t(action === "order" ? "bank.match" : action === "opex" ? "bank.allocate" : "bank.ignore")}
          </Button>
        </form>
      ) : null}
    </Plate>
  );
}
